import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../../../../../environments/environment";
import { Config } from "../../../../core/Config";
import {
	GetDataResponse,
	BindApplicationGroup,
	CreateApplicationGroup,
	CreateRegion,
	SingleAppGroup, SingleRegion, CreateBranch, GetBranchByName, UpdateBranch,
	GetDevGroupByAppGroup,
	BindDeviceAttributeGroup,
	AllEndpoints,
	Endpoint,
	AllDeviceModels,
	DeviceModel,
	FilteredAppGroup,
	Region,
	AllBranches,
	ApplicationAndDeviceGroup,
	AppVersion
} from './system-config';
import { ApiResponse } from '../shared/models/api-response';
import { DeviceAttribute } from "./system-config.model";


const BASE_URL = environment.BASE_URL;

@Injectable({
	providedIn: "root",
})
export class SystemConfigService {
	constructor(
		private httpClient: HttpClient,
		private config: Config
	) { }

	getDeviceAttributes(
		pageIndex?: number,
		pageSize?: number,
		clientId?: number,
		attributeName?: string
	) {
		let params = new HttpParams();
		if (pageIndex) {
			params = params.append("pageIndex", pageIndex);
		}
		if (pageSize) {
			params = params.append("pageSize", pageSize);
		}
		if (clientId) {
			params = params.append("clientId", clientId);
		}
		if (attributeName) {
			params = params.append("attributeName", attributeName);
		}
		return this.httpClient
			.get<ApiResponse<DeviceAttribute[]>>(BASE_URL + this.config.getDeviceAttributes, { params })
			.pipe(
				map((deviceAttributes) => {
					return deviceAttributes;
				})
			);
	}

	createDeviceAttributes(details) {
		return this.httpClient.post(BASE_URL + this.config.createDeviceAttributes, details);
	}

	updateDeviceAttribute(details) {
		return this.httpClient.post(BASE_URL + this.config.updateDeviceAttributes, details);
	}

	deleteDeviceAttribute(code: string, clientId: number) {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.post(BASE_URL + this.config.deleteDeviceAttributes + '/' + code, null, { params });
	}
	getEndpoints(pageNo: number, pageSize: number, clientId?: number): Observable<AllEndpoints> {
		let params = new HttpParams();
		if (pageNo) {
			params = params.append("pageNo", pageNo);
		}
		if (pageSize) {
			params = params.append("pageSize", pageSize);
		}
		if (clientId) {
			params = params.append("clientId", clientId);
		}
		return this.httpClient
			.get<AllEndpoints>(BASE_URL + this.config.getEndpoints, { params })
			.pipe(
				map((endpoints) => {
					return endpoints;
				})
			);
	}

	getEndpointByHostName(hostName: string, clientId?: number): Observable<Endpoint> {
		let params = new HttpParams()
		if (clientId) {
			params = params.append("clientId", clientId)
		}
		return this.httpClient.get<Endpoint>(BASE_URL + this.config.getEndpointByHostName + hostName, { params }).pipe(
			map((endpoint) => {
				return endpoint;
			})
		)
	}

	createEndpoint(details) {
		return this.httpClient.post(BASE_URL + this.config.createEndpoint, details);
	}
	updateEndpoint(details) {
		return this.httpClient.patch(BASE_URL + this.config.updateEndpoint, details);
	}
	deleteEndpoint(code: string, clientId: number) {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.post(BASE_URL + this.config.deleteEndpoint + code, null, { params });
	}

	getDeviceModels(pageIndex, pageSize): Observable<AllDeviceModels> {
		let params = new HttpParams();
		if (pageIndex) {
			params = params.append("pageIndex", pageIndex);
		}
		if (pageSize) {
			params = params.append("pageSize", pageSize);
		}
		return this.httpClient
			.get<AllDeviceModels>(BASE_URL + this.config.getDeviceModels, { params })
			.pipe(
				map((models) => {
					return models;
				})
			);
	}

	getDeviceModelByName(modelName: string): Observable<DeviceModel> {
		return this.httpClient.get<DeviceModel>(BASE_URL + this.config.getDeviceModelByName + modelName).pipe(
			map((deviceModel) => {
				return deviceModel;
			})
		)
	}

	createDeviceModel(details) {
		return this.httpClient.post(BASE_URL + this.config.createDeviceModel, details);
	}
	updateDeviceModel(details) {
		return this.httpClient.put(BASE_URL + this.config.updateDeviceModel, details);
	}
	deleteDeviceModel(code: string) {
		return this.httpClient.post(BASE_URL + this.config.deleteDeviceModel + code, {});
	}



	/*
	**App Management Endpoints
	*/
	getApps(pageIndex, pageSize, searchParams?) {
		let params = new HttpParams();

		if (pageIndex) {
			params = params.set("pageIndex", pageIndex);
		}
		if (pageSize) {
			params = params.set("pageSize", pageSize);
		}
		if (searchParams?.appName) {
			params = params.set("appName", searchParams.appName);
		}


		return this.httpClient
			.get(BASE_URL + this.config.getApps, { params })
			.pipe(
				map((apps) => {
					return apps;
				})
			);
	}
	getAppVersions(pageIndex: number, pageSize: number, clientId?: number) {
		let params = new HttpParams();

		if (pageIndex) {
			params = params.append("pageIndex", pageIndex);
		}
		if (pageSize) {
			params = params.append("pageSize", pageSize);
		}
		if (clientId) {
			params = params.append("clientId", clientId);
		}

		return this.httpClient
			.get<ApiResponse<AppVersion[]>>(BASE_URL + this.config.getAppVersions, { params })
			.pipe(
				map((apps) => {
					return apps;
				})
			);
	}

	getAppsReport(appName: string, oemName: string, pageIndex: number, pageSize: number, clientId?: number) {
		let params = new HttpParams()
			.append("appName", appName)
			.append("oemName", oemName)
			.append("pageIndex", pageIndex)
			.append("pageSize", pageSize)
			.append("clientId", clientId);
		return this.httpClient
			.get(BASE_URL + this.config.getAppsReport, { params })
			.pipe(
				map((appsReport) => {
					return appsReport;
				})
			);
	}

	getApp(appName) {
		return this.httpClient
			.get(BASE_URL + this.config.getApp + '/' + appName)
			.pipe(
				map((apps) => {
					return apps;
				})
			);
	}


	createApp(appDetails: FormData) {
		return this.httpClient.post(BASE_URL + this.config.createApps, appDetails, {
			reportProgress: true,
			observe: 'events'
		});
	}
	updateApp(appDetails: FormData) {
		return this.httpClient.post(BASE_URL + this.config.updateApps, appDetails, {
			reportProgress: true,
			observe: 'events'
		});
	}
	updateAppDetails(appDetails: object) {
		return this.httpClient.post(BASE_URL + this.config.updateAppDetails, appDetails);
	}
	deleteApp(id: number, clientId: number) {
		let params = new HttpParams()
			.append("id", id)
			.append("clientId", clientId)
		return this.httpClient.post(BASE_URL + this.config.deleteApps, null, { params });
	}
	/*
	**Attribute Groups Management Endpoints
	*/
	getDeviceAttributeGroups(
		pageIndex?: number,
		pageSize?: number,
		clientId?: number,
		attributeGroupName?: string
	) {
		let params = new HttpParams();
		if (pageIndex) {
			params = params.append("pageIndex", pageIndex);
		}
		if (pageSize) {
			params = params.append("pageSize", pageSize);
		}
		if (clientId) {
			params = params.append("clientId", clientId);
		}
		if (attributeGroupName) {
			params = params.append("attributeGroupName", attributeGroupName);
		}
		return this.httpClient
			.get(BASE_URL + this.config.getDeviceAttributeGroups, { params })
			.pipe(
				map((apps) => {
					return apps;
				})
			);
	}



	createDeviceAttributeGroup(appDetails) {
		return this.httpClient.post(BASE_URL + "/v1/deviceAttribute/createDeviceAttributeGroup", appDetails);
	}

	updateDeviceAttributeGroup(appDetails) {
		return this.httpClient.post(BASE_URL + this.config.manageDeviceAttributeGroups, appDetails);
	}

	deleteDeviceAttributeGroup(code: string) {
		let params = new HttpParams();
		if (code) {
			params = params.append("appName", code);
		}
		return this.httpClient.post(BASE_URL + this.config.deleteApps + '/' + code, {});
	}

	// application group

	getAllApplicationGroups(pageIndex: number, pageSize: number): Observable<GetDataResponse<ApplicationAndDeviceGroup>> {
		let params = new HttpParams()
			.append("pageIndex", pageIndex)
			.append("pageSize", pageSize);
		return this.httpClient.get<GetDataResponse<ApplicationAndDeviceGroup>>(BASE_URL + this.config.getAllApplicationGroups, { params })
			.pipe(
				map((appGroups) => {
					return appGroups;
				})
			);
	}
	createApplicationGroup(appGroup: CreateApplicationGroup) {
		return this.httpClient.post(BASE_URL + this.config.createApplicationGroup, appGroup);
	}
	deleteApplicationGroup(id, clientId: number) {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.delete(BASE_URL + this.config.deleteApplicationGroupById + "/" + id, { params });
	}
	getApplicationGroupById(id: number): Observable<SingleAppGroup> {
		return this.httpClient.get<SingleAppGroup>(BASE_URL + this.config.getApplicationGroupById + "/" + id)
			.pipe(
				map((appGroup) => {
					return appGroup;
				})
			)
	}
	getApplicationGroupByName(name: string, clientId?: number): Observable<FilteredAppGroup> {
		let params = new HttpParams()
		if (clientId) {
			params = params.append("clientId", clientId)
		}
		return this.httpClient.get<FilteredAppGroup>(BASE_URL + this.config.getApplicationGroupByName + name, { params }).pipe(
			map((appGroup) => {
				return appGroup;
			})
		)
	}
	updateApplicationGroup(appGroup: CreateApplicationGroup, id: number) {
		let params = new HttpParams()
			.append("id", id);
		return this.httpClient.patch(BASE_URL + this.config.updateApplicationGroup, appGroup, { params });
	}
	bindApplicationGroupToDeviceGroup(bindSerial: BindApplicationGroup) {
		return this.httpClient.patch(BASE_URL + this.config.bindApplicationGroupToDeviceGroup, bindSerial);
	}
	unbindApplicationGroupFromDeviceGroup(unbindSerial: BindApplicationGroup) {
		return this.httpClient.patch(BASE_URL + this.config.unbindApplicationGroupFromDeviceGroup, unbindSerial);
	}
	getDeviceGroupByAppGroup(applicationGroupId: number, pageIndex: number, pageSize: number): Observable<GetDevGroupByAppGroup> {
		let params = new HttpParams()
			.append("pageIndex", pageIndex)
			.append("pageSize", pageSize);
		return this.httpClient.get<GetDevGroupByAppGroup>(BASE_URL + this.config.getDevGroupByAppGroup + "/" + applicationGroupId, { params })
			.pipe(
				map((appDevice) => {
					return appDevice;
				})
			);
	}
	createRegion(regionName: CreateRegion) {
		return this.httpClient.post(BASE_URL + this.config.createRegion, regionName);
	}
	getRegionByName(regionName) {
		return this.httpClient.get(BASE_URL + this.config.getRegion + "/" + regionName)
			.pipe(
				(region) => {
					return region;
				}
			);
	}
	getAllRegions(pageIndex, pageSize): Observable<GetDataResponse<Region>> {
		let params = new HttpParams()
			.append("pageIndex", pageIndex)
			.append("pageSize", pageSize);
		return this.httpClient.get<GetDataResponse<Region>>(BASE_URL + this.config.getAllRegions, { params })
			.pipe(
				map((regions) => {
					return regions;
				})
			);
	}
	deleteRegion(regionName, clientId: number) {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.delete(BASE_URL + this.config.deleteRegion + "/" + regionName, { params });
	}
	getRegion(regionName: string, clientId?: number): Observable<SingleRegion> {
		let params = new HttpParams();
		if (clientId) {
			params = params.append("clientId", clientId);
		}
		return this.httpClient.get<SingleRegion>(BASE_URL + this.config.getRegion + "/" + regionName, { params })
			.pipe(
				map((region) => {
					return region;
				})
			)
	}
	createBranch(branchData: CreateBranch) {
		return this.httpClient.post(BASE_URL + this.config.createBranch, branchData);
	}
	getBranchByName(branchName, clientId: number): Observable<GetBranchByName> {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.get<GetBranchByName>(BASE_URL + this.config.getBranchByName + "/" + branchName, { params })
			.pipe(
				map((branch) => {
					return branch;
				})
			)
	}
	deleteBranch(branchName, clientId: number) {
		let params = new HttpParams()
			.append("clientId", clientId);
		return this.httpClient.delete(BASE_URL + this.config.deleteBranch + "/" + branchName, { params });
	}
	getAllBranchesByRegion(pageIndex, pageSize, regionName, clientId: number): Observable<GetDataResponse<AllBranches>> {
		let params = new HttpParams()
			.append("pageIndex", pageIndex)
			.append("pageSize", pageSize)
			.append("regionName", regionName)
			.append("clientId", clientId);
		return this.httpClient.get<GetDataResponse<AllBranches>>(BASE_URL + this.config.getAllBranchesByRegion, { params })
			.pipe(
				map((branches) => {
					return branches;
				})
			)
	}
	updateBranch(branchData: UpdateBranch) {
		return this.httpClient.patch(BASE_URL + this.config.updateBranch, branchData);
	}

	bindDeviceAttributeGroup(request: BindDeviceAttributeGroup) {
		return this.httpClient.post(BASE_URL + this.config.bindDeviceAttributeGroup, request)
	}
}
